import React, { useMemo } from 'react';
import { FormDialog, RhfTextField, toast } from '@core/components';
import {
  type MaterialType,
  createMaterialType,
  updateMaterialType,
} from '../../api/materialType';
import type { MaterialKind } from '../enum/materialKind';

interface MaterialTypeDialogProps {
  open: boolean;
  onClose: () => void;
  kind: MaterialKind;
  label: string;
  materialType?: MaterialType | null;
  inputValue?: string;
  onSuccess?: (materialType: MaterialType) => void;
}

type MaterialTypeFormValues = {
  name: string;
};

const MaterialTypeDialog: React.FC<MaterialTypeDialogProps> = ({
  open,
  onClose,
  kind,
  label,
  materialType,
  inputValue,
  onSuccess,
}) => {
  const defaultValues = useMemo<MaterialTypeFormValues>(
    () => ({ name: materialType?.name ?? inputValue?.trim() ?? '' }),
    [materialType, inputValue]
  );

  const onSubmit = async (data: MaterialTypeFormValues) => {
    const payload = { kind, name: data.name.trim() };
    try {
      const saved = materialType
        ? await updateMaterialType(materialType.id, payload)
        : await createMaterialType(payload);
      toast.success(materialType ? `${label} updated` : `${label} added`);
      onSuccess?.(saved);
    } catch (error) {
      toast.apiError(`Failed to save ${label.toLowerCase()}.`, error);
      throw error;
    }
  };

  return (
    <FormDialog<MaterialTypeFormValues>
      open={open}
      onClose={onClose}
      title={materialType ? `Rename ${label}` : `Add ${label}`}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      submitText={materialType ? 'Update' : 'Add'}
      maxWidth="xs"
    >
      <RhfTextField<MaterialTypeFormValues> name="name" label="Name" required />
    </FormDialog>
  );
};

export default MaterialTypeDialog;
